import { useEffect, useState } from 'react'
import { getColumns } from '../../utils/helpers.ts'
import GalleryItem from './GalleryItem'
import Slideshow from '../Slideshow.tsx'
import type { Rally } from '../../data/rallies'

type Image = {
  src: string
  city: string
  country?: string
  // translation key shown as the overlay instead of the city's date and place
  label?: string
  // artist credit, always shown in the overlay
  author?: string
  // e.g. the organisers' page, shown as a small icon next to the city name
  link?: string
}[]

interface Props {
  id: string
  images: Image
  collapseToFirstRow?: boolean
  height?: string
  rally?: Rally
  // show a download button on each image
  downloadable?: boolean
  // no section padding or background, for use inside another page section
  bare?: boolean
}

export default function Gallery(props: Props) {
  const { images, collapseToFirstRow = false } = props
  const [expanded, setExpanded] = useState(!collapseToFirstRow)
  const [columns, setColumns] = useState(1)

  useEffect(() => {
    const update = () => setColumns(getColumns())
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const visibleImages = expanded ? images : images.slice(0, columns)

  function handleToggle() {
    if (expanded) {
      const el = document.getElementById('rally-schedule')
      if (el) el.scrollIntoView()
    }
    setExpanded((v) => !v)
  }

  return (
    <section
      id={`${props.id}-gallery`}
      className={
        props.bare
          ? 'w-full'
          : 'flex flex-col gap-10 px-6 md:px-20 py-25 bg-gray-50 text-center text-xl md:text-2xl leading-relaxed opacity-90 items-center'
      }
    >
      <div className={props.bare ? 'w-full relative' : 'w-full px-4 py-12 relative'}>
        <div
          className='
            grid gap-4
            grid-cols-1 
            sm:grid-cols-2 
            md:grid-cols-3
            lg:grid-cols-4
          '
        >
          {visibleImages.length > 1 ? (
            <>
              {visibleImages.map((img, i) => (
                <GalleryItem
                  key={`${img.city}-${i}`}
                  src={img.src}
                  city={img.city}
                  label={img.label}
                  author={img.author}
                  link={img.link}
                  height={props.height}
                  rally={props.rally}
                  downloadable={props.downloadable}
                />
              ))}
            </>
          ) : (
            <Slideshow images={props.images.map((img) => img.src)} interval={1500} />
          )}
        </div>

        {collapseToFirstRow && images.length > columns && (
          <div className={`${expanded ? 'sticky' : ''} bottom-4 mt-8 flex justify-center`}>
            <button
              onClick={handleToggle}
              className='
                rounded-full
                bg-blue-800 text-white
                px-6 py-3
                text-sm md:text-base
                shadow-lg
                hover:bg-blue-700
                cursor-pointer
                transition
              '
            >
              {expanded ? 'See less' : 'See more'}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
